import React, { ChangeEvent, useState } from "react";
import { useLayout } from "../../hooks/use-layout";

import { BrregSearch, Button, Theme, listItem } from "@in/component-library";

interface AccountSearchProps {
  onClose: () => void;
  selectAccount: (accountId: string, isExternal: boolean) => void;
  accountInfo: (organizationNumber: string, address: string, name: string) => void;
  isSaving: boolean;
  toCreateForeignAccount: () => void;
}

const DebounceTimeoutMilliseconds = 500;
const BrregBaseUrl = "https://data.brreg.no/enhetsregisteret/api/enheter";

const AccountSearch = ({
  onClose,
  selectAccount,
  accountInfo,
  isSaving,
  toCreateForeignAccount
}: AccountSearchProps) => {
  const { translations } = useLayout();
  const [searchResults, setSearchResults] = useState<listItem[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<listItem>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);

  const search = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
    isOrganizationNumber(e.target.value)
      ? fetch(`${BrregBaseUrl}/${e.target.value}`)
          .then((res) => res.json())
          .then((entity) => {
            setSearchResults([mapEntity(entity)]);
          })
      : fetch(`${BrregBaseUrl}?navn=${e.target.value}&konkurs=false`)
          .then((res) => res.json())
          .then((res) => {
            const results = res?._embedded?.enheter?.map((entity: any) => mapEntity(entity));
            setSearchResults(results !== undefined ? results : []);
          });
  };

  const isOrganizationNumber = (value: string) => /^\d{9}$/.test(value);

  const mapEntity = (entity: any): listItem => ({
    organizationNumber: entity.organisasjonsnummer,
    name: entity.navn,
    address:
      entity.forretningsadresse?.postnummer && entity.forretningsadresse?.kommune
        ? `${entity.forretningsadresse.postnummer} ${entity.forretningsadresse.kommune}`
        : ""
  });

  const listItemOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const match = searchResults.find((element) => element.organizationNumber === e.currentTarget.value);
    setSelectedAccount(match);
    setSearchResults([]);
    setSearchTerm("");
    setErrorMessage("");
    setDisabled(false);
  };

  const toAddAccount = () => {
    if (selectedAccount) {
      selectAccount(selectedAccount.organizationNumber, true);
      accountInfo(selectedAccount.organizationNumber, selectedAccount.address, selectedAccount.name);
    } else {
      setErrorMessage(translations.brregNotRegisteredErrorMessage);
      setDisabled(true);
    }
  };

  return (
    <>
      <p className="account-search__text">{translations.chooseAccountChooseOtherLabel}</p>

      <div className={selectedAccount && "account-search__input"}>
        <BrregSearch
          className={errorMessage && "account-search__error"}
          id="orgNoSearch"
          name="orgNoSearch"
          label={translations.chooseAccountFindCompanyLabel}
          options={searchResults}
          value={searchTerm}
          selected={selectedAccount ? [selectedAccount] : []}
          onChange={search}
          listItemOnClick={listItemOnClick}
          companyCardOnClick={() => setSelectedAccount(undefined)}
          debounce={true}
          debounceTime={DebounceTimeoutMilliseconds}
          autoComplete="organization"
          errorMsg={errorMessage}
        />
      </div>
      <div className="account-search__foreign-account">
        <p>{translations.chooseAccountForeignAccountLabel}</p>
        <Button variant="link" theme={Theme.Neutral} onClick={toCreateForeignAccount}>
          {translations.chooseAccountAddForeignAccount}
        </Button>
      </div>
      <div aria-live="polite" className="account-search__button">
        <Button
          variant="solid"
          theme={Theme.Neutral}
          disabled={isSaving || disabled}
          isLoading={isSaving}
          onClick={toAddAccount}
        >
          {translations.chooseAccountNextLabel}
        </Button>
        <Button variant="outlined" theme={Theme.Neutral} onClick={onClose}>
          {translations.personalOrExternalAccountsCancelButton}
        </Button>
      </div>
    </>
  );
};

export default AccountSearch;
